import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { ApiError } from '@/shared/lib/api-client';
import { confirm } from '@/shared/lib/confirm';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  BarChart3,
  Copy,
  Eye,
  FileText,
  Loader2,
  Megaphone,
  Pause,
  Play,
  Plus,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  type CampaignDraftState,
  type CampaignRow,
  deleteCampaign,
  deleteCampaignDraft,
  getCampaign,
  getCampaignDraft,
  listCampaignDrafts,
  listCampaigns,
  pauseCampaign,
  resumeCampaign,
} from './api';
import { CampaignWizardModal } from './CampaignWizardModal';
import { MensagemDaCampanhaModal } from './MensagemDaCampanhaModal';
import { StatusChip, fmtDateTime, num } from './campaign-ui';
import { estadoDaCopia } from './copia-de-campanha';

const PAGE_SIZE = 50;

const STATUS_OPTIONS: Array<{ value: CampaignRow['status'] | 'draft' | ''; label: string }> = [
  { value: '', label: 'Todos os status' },
  { value: 'running', label: 'Processando' },
  { value: 'scheduled', label: 'Agendada' },
  { value: 'paused', label: 'Pausada' },
  { value: 'completed', label: 'Concluída' },
  { value: 'errored', label: 'Erro' },
  { value: 'draft', label: 'Rascunho' },
];

export function CampanhasPage() {
  const qc = useQueryClient();
  const [wizard, setWizard] = useState<
    | null
    | { mode: 'new' }
    | { mode: 'draft'; draftId: number; state: CampaignDraftState }
    // Cópia de uma campanha: prefill SEM draftId — nasce como campanha nova.
    | { mode: 'copy'; state: CampaignDraftState }
  >(null);
  /** Campanha cuja configuração está sendo buscada pra duplicar (mostra spinner). */
  const [duplicando, setDuplicando] = useState<number | null>(null);
  /** Campanha com a prévia da mensagem enviada aberta. */
  const [previaDe, setPreviaDe] = useState<number | null>(null);
  const [nameQuery, setNameQuery] = useState('');
  const [debouncedName, setDebouncedName] = useState('');
  const [filterStatus, setFilterStatus] = useState<CampaignRow['status'] | 'draft' | ''>('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(nameQuery), 300);
    return () => clearTimeout(t);
  }, [nameQuery]);

  const campaignStatusFilter =
    filterStatus !== '' && filterStatus !== 'draft' ? filterStatus : undefined;
  const showCampaigns = filterStatus !== 'draft';
  const showDrafts = filterStatus === '' || filterStatus === 'draft';

  const listQ = useInfiniteQuery({
    queryKey: ['campaigns', { q: debouncedName, status: campaignStatusFilter }],
    queryFn: ({ pageParam }) =>
      listCampaigns({
        q: debouncedName || undefined,
        status: campaignStatusFilter,
        limit: PAGE_SIZE,
        ...(typeof pageParam === 'string' && { cursor: pageParam }),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    placeholderData: (prev) => prev,
    gcTime: 0,
    enabled: showCampaigns,
    // Progresso em (quase) tempo real: refaz a cada 2,5s ENQUANTO houver
    // campanha em estado que muda sozinho — `draft` (recém "enviar agora",
    // transitório até o dispatcher pegar → running), `scheduled` (espera o cron)
    // e `running`. Para quando tudo terminou — sem polling à toa.
    // `inBackground` cobre aba/janela sem foco.
    refetchInterval: (query) => {
      const active = query.state.data?.pages?.some((p) =>
        p.campaigns.some(
          (c) => c.status === 'draft' || c.status === 'scheduled' || c.status === 'running',
        ),
      );
      return active ? 2500 : false;
    },
    refetchIntervalInBackground: true,
  });
  const draftsQ = useQuery({ queryKey: ['campaign-drafts'], queryFn: listCampaignDrafts });

  const openDraft = async (id: number): Promise<void> => {
    const draft = await getCampaignDraft(id);
    setWizard({ mode: 'draft', draftId: id, state: draft.state });
  };

  const invalidateCampaigns = (): void => {
    qc.invalidateQueries({ queryKey: ['campaigns'] });
  };
  const onActionError = (err: unknown): void => {
    if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    else toast.error('Falha na ação');
  };

  // Duplicar: busca a configuração completa e abre o wizard preenchido (sem
  // destinatários). Nada é gravado até a pessoa concluir ou salvar rascunho.
  const duplicar = async (id: number): Promise<void> => {
    setDuplicando(id);
    try {
      setWizard({ mode: 'copy', state: estadoDaCopia(await getCampaign(id)) });
    } catch (err) {
      onActionError(err);
    } finally {
      setDuplicando(null);
    }
  };
  const pauseMut = useMutation({
    mutationFn: (id: number) => pauseCampaign(id),
    onSuccess: invalidateCampaigns,
    onError: onActionError,
  });
  const resumeMut = useMutation({
    mutationFn: (id: number) => resumeCampaign(id),
    onSuccess: invalidateCampaigns,
    onError: onActionError,
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteCampaign(id),
    onSuccess: invalidateCampaigns,
    onError: onActionError,
  });
  const deleteDraftMut = useMutation({
    mutationFn: (id: number) => deleteCampaignDraft(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaign-drafts'] }),
    onError: onActionError,
  });

  const campaigns = useMemo<CampaignRow[]>(
    () => (showCampaigns ? (listQ.data?.pages.flatMap((p) => p.campaigns) ?? []) : []),
    [listQ.data, showCampaigns],
  );
  const total = listQ.data?.pages[0]?.total ?? 0;

  const nameNorm = debouncedName.trim().toLowerCase();
  const visibleDrafts = showDrafts
    ? (draftsQ.data ?? []).filter((d) => !nameNorm || (d.name || '').toLowerCase().includes(nameNorm))
    : [];

  const hasFilter = nameNorm !== '' || filterStatus !== '';
  const isEmpty =
    !listQ.isLoading && !draftsQ.isLoading && campaigns.length === 0 && visibleDrafts.length === 0;

  // IntersectionObserver no final da lista — idêntico a Contatos.
  const sentinelRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !listQ.hasNextPage || listQ.isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) void listQ.fetchNextPage();
        }
      },
      { rootMargin: '200px', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [listQ.hasNextPage, listQ.isFetchingNextPage, listQ.fetchNextPage]);

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden bg-n-50">
      {/* Header */}
      <header className="flex flex-shrink-0 items-center justify-between border-b border-n-200 bg-n-0 px-5 py-4">
        <h1 style={{ fontSize: '22px', fontWeight: 700, letterSpacing: '-.02em', color: 'var(--n-900)' }}>
          Campanhas
        </h1>
        <Button onClick={() => setWizard({ mode: 'new' })}>
          <Plus aria-hidden="true" />
          Nova campanha WABA
        </Button>
      </header>

      {/* Filtros — barra fixa (nome + status) */}
      <div className="flex-shrink-0 border-b border-n-200 bg-n-0 px-5 py-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="flex flex-col gap-1.5 sm:col-span-2">
            <FieldLabel>Nome da campanha</FieldLabel>
            <Input
              value={nameQuery}
              onChange={(e) => setNameQuery(e.target.value)}
              placeholder="Buscar por nome…"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <FieldLabel>Status</FieldLabel>
            <select
              value={filterStatus}
              onChange={(e) =>
                setFilterStatus(e.target.value as CampaignRow['status'] | 'draft' | '')
              }
              className="h-9 w-full rounded-md border border-n-300 bg-n-0 px-2 text-[13px]"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.label} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {/* Área da tabela — scroll DENTRO do card (igual Contatos) */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-5">
        <div className="flex min-h-0 flex-col overflow-hidden rounded-md border border-n-200 bg-n-0 shadow-sm">
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full min-w-[760px] text-[12px]">
              <thead className="sticky top-0 z-10 border-b border-n-200 bg-n-50 text-left">
                <tr>
                  <Th>#</Th>
                  <Th>Campanha</Th>
                  <Th>Início</Th>
                  <Th>Status</Th>
                  <Th align="right">Contatos</Th>
                  <Th>Progresso</Th>
                  <Th align="right">Ações</Th>
                </tr>
              </thead>
              <tbody>
                {listQ.isLoading && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center">
                      <Loader2 size={20} className="mx-auto animate-spin text-n-400" />
                    </td>
                  </tr>
                )}
                {isEmpty && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-n-500">
                      {hasFilter ? 'Nenhuma campanha com esses filtros.' : 'Nenhuma campanha ainda.'}
                    </td>
                  </tr>
                )}

                {visibleDrafts.map((d) => (
                  <tr
                    key={`draft-${d.id}`}
                    className="border-b border-n-100 last:border-b-0 hover:bg-n-50"
                  >
                    <td className="px-[14px] py-[11px] text-n-400">—</td>
                    <td className="px-[14px] py-[11px]">
                      <button
                        type="button"
                        onClick={() => void openDraft(d.id)}
                        className="flex items-center gap-2 font-semibold text-brand-700 hover:underline"
                      >
                        <FileText size={14} className="text-n-400" />
                        {d.name || 'Rascunho sem nome'}
                      </button>
                    </td>
                    <td className="px-[14px] py-[11px] text-n-400">—</td>
                    <td className="px-[14px] py-[11px]">
                      <StatusChip status="draft" />
                    </td>
                    <td className="px-[14px] py-[11px] text-right text-n-400">—</td>
                    <td className="px-[14px] py-[11px] text-n-400">—</td>
                    <td className="px-[14px] py-[11px]">
                      <div className="flex items-center justify-end gap-1">
                        {/* Rascunho: só Excluir. Clicar no nome continua a edição. */}
                        <IconBtn
                          title="Excluir rascunho"
                          danger
                          onClick={async () => {
                            const ok = await confirm({
                              title: 'Excluir rascunho?',
                              description: `O rascunho "${d.name || 'sem nome'}" será removido. Esta ação não pode ser desfeita.`,
                              confirmText: 'Excluir',
                              variant: 'danger',
                            });
                            if (ok) deleteDraftMut.mutate(d.id);
                          }}
                        >
                          <X size={14} />
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                ))}

                {campaigns.map((c) => {
                  // Progresso = PROCESSADO (enviado + falhou) / total → chega a
                  // 100% quando o disparo termina, mesmo com falhas. A falha
                  // vira segmento vermelho na barra (não some do progresso).
                  const successPct =
                    c.total_count > 0
                      ? Math.round(((c.sent + c.delivered + c.read) / c.total_count) * 100)
                      : 0;
                  const failedPct =
                    c.total_count > 0 ? Math.round((c.failed / c.total_count) * 100) : 0;
                  const startIso = c.status === 'scheduled' ? c.scheduled_at : c.started_at;
                  return (
                    <tr key={c.id} className="border-b border-n-100 last:border-b-0 hover:bg-n-50">
                      <td className="px-[14px] py-[11px] font-mono text-n-700">#{c.id}</td>
                      <td className="px-[14px] py-[11px]">
                        <Link
                          // biome-ignore lint/suspicious/noExplicitAny: dynamic route
                          to={'/campanhas/$id' as any}
                          params={{ id: String(c.id) } as any}
                          className="flex items-center gap-2 font-semibold text-brand-700 hover:underline"
                        >
                          <Megaphone size={14} className="text-brand-500" />
                          {c.name}
                        </Link>
                        <span className="mt-0.5 block text-[11px] text-n-500">
                          template:{' '}
                          <code className="rounded bg-n-100 px-1 py-px font-mono text-[10px] text-n-600">
                            {c.template_name}
                          </code>
                        </span>
                      </td>
                      <td className="px-[14px] py-[11px] text-n-600">{fmtDateTime(startIso)}</td>
                      <td className="px-[14px] py-[11px]">
                        <StatusChip status={c.status} />
                      </td>
                      <td className="px-[14px] py-[11px] text-right font-semibold text-n-900">
                        {num(c.total_count)}
                      </td>
                      <td className="px-[14px] py-[11px]">
                        <ProgressMini
                          status={c.status}
                          successPct={successPct}
                          failedPct={failedPct}
                        />
                      </td>
                      <td className="px-[14px] py-[11px]">
                        <div className="flex items-center justify-end gap-1">
                          {/* Pausar (processando) */}
                          {c.status === 'running' && (
                            <IconBtn title="Pausar" onClick={() => pauseMut.mutate(c.id)}>
                              <Pause size={14} />
                            </IconBtn>
                          )}
                          {/* Play — retomar (pausada) ou iniciar agora (agendada) */}
                          {(c.status === 'paused' || c.status === 'scheduled') && (
                            <IconBtn
                              title={c.status === 'scheduled' ? 'Iniciar agora' : 'Retomar'}
                              onClick={() => resumeMut.mutate(c.id)}
                            >
                              <Play size={14} />
                            </IconBtn>
                          )}
                          {/* Prévia da mensagem enviada (bolha WhatsApp) */}
                          <IconBtn title="Ver mensagem" onClick={() => setPreviaDe(c.id)}>
                            <Eye size={14} />
                          </IconBtn>
                          {/* Duplicar — qualquer status. Abre o wizard com a
                              configuração; destinatários são escolhidos de novo. */}
                          <IconBtn title="Duplicar" onClick={() => duplicar(c.id)}>
                            {duplicando === c.id ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Copy size={14} />
                            )}
                          </IconBtn>
                          {/* Excluir — disponível em qualquer status (remove de vez) */}
                          <IconBtn
                            title="Excluir"
                            danger
                            onClick={async () => {
                              const ok = await confirm({
                                title: 'Excluir campanha?',
                                description: `A campanha "${c.name}" será removida permanentemente.`,
                                confirmText: 'Excluir',
                                cancelText: 'Voltar',
                                variant: 'danger',
                              });
                              if (ok) deleteMut.mutate(c.id);
                            }}
                          >
                            <X size={14} />
                          </IconBtn>
                          {/* Estatísticas — todas menos rascunho (transitório).
                              Agendada mostra: dá pra ver os destinatários pendentes. */}
                          {c.status !== 'draft' && (
                            <Link
                              // biome-ignore lint/suspicious/noExplicitAny: dynamic route
                              to={'/campanhas/$id' as any}
                              params={{ id: String(c.id) } as any}
                              title="Estatísticas"
                              aria-label="Estatísticas"
                              className="flex h-[30px] w-[30px] items-center justify-center rounded-sm text-n-500 hover:bg-n-100 hover:text-n-800"
                            >
                              <BarChart3 size={14} />
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {/* Sentinela: IntersectionObserver dispara fetchNextPage. */}
                {listQ.hasNextPage && (
                  <tr ref={sentinelRef}>
                    <td colSpan={7} className="py-4 text-center text-[12px] text-n-500">
                      {listQ.isFetchingNextPage ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 size={14} className="animate-spin" /> Carregando mais…
                        </span>
                      ) : (
                        'Role para carregar mais'
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer fora do scroll */}
          {showCampaigns && campaigns.length > 0 && (
            <div className="flex items-center justify-between border-t border-n-100 px-4 py-[10px] text-[12px] text-n-500">
              <span>
                {campaigns.length >= total
                  ? `${num(total)} campanha(s)`
                  : `Mostrando ${num(campaigns.length)} de ${num(total)} campanhas`}
              </span>
            </div>
          )}
        </div>
      </div>

      {wizard && (
        <CampaignWizardModal
          onClose={() => setWizard(null)}
          {...(wizard.mode === 'draft' && {
            draftId: wizard.draftId,
            initialState: wizard.state,
          })}
          {...(wizard.mode === 'copy' && {
            initialState: wizard.state,
            title: 'Duplicar campanha',
          })}
        />
      )}
      {previaDe != null && (
        <MensagemDaCampanhaModal campanhaId={previaDe} onClose={() => setPreviaDe(null)} />
      )}
    </div>
  );
}

// ─── Subcomponentes ───────────────────────────────────────────────────────
function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-semibold text-[11px] uppercase tracking-[0.05em] text-n-500">
      {children}
    </span>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th
      className={`whitespace-nowrap px-[14px] py-[10px] font-bold text-[11px] uppercase tracking-[.05em] text-n-500 ${
        align === 'right' ? 'text-right' : ''
      }`}
    >
      {children}
    </th>
  );
}

function ProgressMini({
  status,
  successPct,
  failedPct,
}: {
  status: CampaignRow['status'];
  successPct: number;
  failedPct: number;
}) {
  if (status === 'scheduled') {
    return <span className="text-[11px] text-n-500">aguardando</span>;
  }
  // Segmento de sucesso: verde quando concluída, âmbar pausada, brand rodando.
  const successFill =
    status === 'completed'
      ? 'bg-success-500'
      : status === 'paused'
        ? 'bg-warning-500'
        : 'bg-brand-500';
  const processed = Math.min(100, successPct + failedPct);
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-[6px] min-w-[80px] flex-1 overflow-hidden rounded-pill bg-n-100">
        <div className={`h-full ${successFill}`} style={{ width: `${successPct}%` }} />
        <div className="h-full bg-danger-500" style={{ width: `${failedPct}%` }} />
      </div>
      <span className="whitespace-nowrap font-semibold text-[11px] text-n-700">
        {processed}%{failedPct > 0 ? ` · ${failedPct}% falha` : ''}
      </span>
    </div>
  );
}

function IconBtn({
  children,
  title,
  onClick,
  danger,
}: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className={`flex h-[30px] w-[30px] items-center justify-center rounded-sm text-n-500 ${
        danger ? 'hover:bg-danger-50 hover:text-danger-600' : 'hover:bg-n-100 hover:text-n-800'
      }`}
    >
      {children}
    </button>
  );
}
